// Страница за създаване/редакция на правило за взаимозаменяемост
// (`/inventory/interchangeables/new` и `/inventory/interchangeables/:groupId/edit`).
//
// Правилото е РАВЕНСТВО между продукти, които се заместват взаимно, когато
// някой липсва (при разнасяне на отчет и при инвентаризация):
//   • основна колона — веригата „1 кг Кафе A = 0.7 кг Кафе B = …" от карти с
//     количество, празното място с търсачката и „Целият списък", а при 3+
//     продукта — кой се изписва пръв (`InterchangeRuleChain`);
//   • странична колона (на десктоп лепкава) — готовността за запис, живият
//     пример „Ако липсват 10 кг … → …" (`InterchangeRulePreview`) и коментарът.
// Под 1024 px — една колона, примерът е след продуктите.
//
// Payload-ът е непроменен: групата е ОБЩА за всички складове (без
// warehouseId), поне 2 продукта с положително количество; количествата се
// превеждат в записаните коефициенти в `interchangeRuleMath.ts`.
// Клавиатура: писане отвсякъде влиза в търсачката, ↓ „отникъде" — първото
// количество, Esc „отникъде" = назад към списъка (с пазача), Ctrl/Cmd+Enter =
// запис; ↑/↓/Enter между количествата и Alt+↑/↓ — във веригата.
import { useEffect, useMemo, useRef, useState } from "react";
import { App, Input, Spin } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import {
  useCreateInterchangeGroup,
  useDeleteInterchangeGroup,
  useInterchangeGroups,
  useProducts,
  useUpdateInterchangeGroup,
} from "../../../hooks/useInventory";
import type { Product } from "../../../api/inventory";
import { InventoryTheme } from "../components/InventoryTheme";
import { KbdChip } from "../components/KbdChip";
import { PageHeader } from "../components/list/PageHeader";
import {
  DANGER_TEXT_BUTTON_CLASS,
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
} from "../components/list/buttonStyles";
import { useUnitDecimals } from "../components/qty";
import { pickableProducts } from "../components/product/deletedProducts";
import { CatalogPickerModal } from "../components/search/CatalogPickerModal";
import { useWorksheetKeyboard } from "../components/useWorksheetKeyboard";
import { useConfirm } from "../components/confirm/useConfirm";
import { useLeaveConfirm } from "../components/confirm/useLeaveConfirm";
import { InterchangeRuleChain } from "./components/InterchangeRuleChain";
import { InterchangeRulePreview } from "./components/InterchangeRulePreview";
import { amountInputId } from "./components/interchangeablesShared";
import { useInterchangeRuleDraft } from "./components/useInterchangeRuleDraft";

const LIST_PATH = "/inventory/interchangeables";

type Props = {
  mode: "new" | "edit";
  // Бутонът за менюто на тесен екран е в PageHeader (useInventoryShell)
  onOpenMobileMenu?: () => void;
  isNarrow?: boolean;
  // Регистрира пазач за незапазени промени в layout-а (Inventory.tsx), за да
  // могат sidebar стрелката/менюто да питат преди напускане.
  onRegisterLeaveGuard?: (
    guard: ((proceed: () => void) => void) | null,
  ) => void;
};

const errorDetailOf = (error: unknown) =>
  (error as { response?: { data?: { detail?: string } } }).response?.data?.detail;

export const InterchangeableRulePage = ({
  mode,
  isNarrow = false,
  onRegisterLeaveGuard,
}: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { isDesktop } = useViewport();
  const decimalsOf = useUnitDecimals();

  const { data: products = [] } = useProducts();
  const { data: groups = [], isLoading: groupsLoading } = useInterchangeGroups();
  const createMutation = useCreateInterchangeGroup();
  const updateMutation = useUpdateInterchangeGroup();
  const deleteMutation = useDeleteInterchangeGroup();

  // ─── Пазач за незапазени промени ─────────────────────────────────────────
  const dirtyRef = useRef(false);
  const draft = useInterchangeRuleDraft(() => {
    dirtyRef.current = true;
  });

  const [search, setSearch] = useState("");
  const [catalogOpen, setCatalogOpen] = useState(false);
  // Огледало на текста в търсачката: машината за писане (по-долу) трябва да
  // знае текущата стойност СИНХРОННО, преди React да е пребоядисал полето.
  const searchRef = useRef("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const updateSearch = (value: string) => {
    searchRef.current = value;
    setSearch(value);
  };
  const focusSearch = () => searchInputRef.current?.focus();
  const loadedRef = useRef(false);
  const autoFocusedRef = useRef(false);

  const editedGroup = useMemo(
    () =>
      mode === "edit"
        ? (groups.find((group) => group.id === Number(groupId)) ?? null)
        : null,
    [mode, groupId, groups],
  );

  // Префил при редакция — веднъж, щом групата пристигне (зареждането НЕ вдига
  // флага за незапазени промени).
  useEffect(() => {
    if (mode !== "edit" || loadedRef.current || !editedGroup) return;
    loadedRef.current = true;
    draft.load(editedGroup);
  }, [mode, editedGroup, draft]);

  // Ново правило на десктоп: фокусът е в „Продукт 1" — първото, което се прави
  useEffect(() => {
    if (autoFocusedRef.current || mode !== "new" || !isDesktop) return;
    autoFocusedRef.current = true;
    searchInputRef.current?.focus();
  }, [mode, isDesktop]);

  // „Излизане от правилото „A = B" без запис?" — продуктите му в прегледа
  const confirm = useConfirm();
  const confirmLeave = useLeaveConfirm();
  const ruleName = draft.members.map((member) => member.productName).join(" = ");
  const memberItems = draft.members.map((member, index) => ({
    key: index,
    label: member.productName,
    value: member.unit ?? undefined,
  }));
  const leaveAskingRef = useRef(false);
  const requestLeave = useRef((proceed: () => void) => proceed());
  requestLeave.current = (proceed: () => void) => {
    if (!dirtyRef.current) {
      proceed();
      return;
    }
    if (leaveAskingRef.current) return;
    leaveAskingRef.current = true;
    void confirmLeave({
      document:
        mode === "new" || !ruleName
          ? t("inventoryPage.ichDocNewRule")
          : t("inventoryPage.ichDocEditRule", { name: ruleName }),
      isNew: mode === "new",
      items: memberItems,
    }).then((confirmed) => {
      leaveAskingRef.current = false;
      if (confirmed) proceed();
    });
  };
  useEffect(() => {
    const guard = (proceed: () => void) => requestLeave.current(proceed);
    onRegisterLeaveGuard?.(guard);
    return () => onRegisterLeaveGuard?.(null);
  }, [onRegisterLeaveGuard]);
  const leaveToList = () => requestLeave.current(() => navigate(LIST_PATH));

  // ─── Продукти ────────────────────────────────────────────────────────────
  const productsById = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products],
  );
  const availableProducts = useMemo(() => {
    const chosen = new Set(draft.members.map((member) => member.productId));
    // изтритите в организацията не се предлагат (членовете на групата пазят имената си)
    return pickableProducts(products).filter((product) => !chosen.has(product.id));
  }, [products, draft.members]);
  // „Целият списък" — само продуктите, които още не са в групата
  const catalogItems = useMemo(
    () =>
      availableProducts.map((product) => ({
        id: product.id,
        name: product.name,
        group: product.category ?? null,
        meta: product.unit ?? "",
        added: false,
      })),
    [availableProducts],
  );

  const addProducts = (added: Product[]) => {
    if (!added.length) return;
    focusSearch();
    draft.addProducts(added);
  };
  const addFromCatalog = (ids: number[]) => {
    setCatalogOpen(false);
    addProducts(
      ids.map((id) => productsById.get(id)).filter((product): product is Product => !!product),
    );
  };

  // ─── Готовност и запис ───────────────────────────────────────────────────
  const issues: string[] = [];
  if (!draft.hasEnoughProducts) issues.push(t("inventoryPage.interchangeNeedTwo"));
  if (draft.membersWithoutAmount.length)
    issues.push(
      t("inventoryPage.ichMissingAmount", {
        names: draft.membersWithoutAmount.map((member) => member.productName).join(", "),
      }),
    );

  const saving = createMutation.isPending || updateMutation.isPending;

  const submit = () => {
    if (saving) return;
    const payload = draft.payload;
    if (!payload) return message.error(issues[0] ?? t("inventoryPage.errSave"));
    const onSuccess = () => {
      dirtyRef.current = false;
      message.success(t("inventoryPage.interchangeSaved"));
      navigate(LIST_PATH);
    };
    const onError = (error: unknown) =>
      message.error(errorDetailOf(error) || t("inventoryPage.errSave"));
    if (editedGroup) {
      updateMutation.mutate(
        { id: editedGroup.id, payload },
        { onSuccess, onError },
      );
    } else {
      createMutation.mutate(payload, { onSuccess, onError });
    }
  };

  const requestDelete = () => {
    if (!editedGroup) return;
    // Правилото не е складов документ: изтриването не пипа наличности и минали
    // документи — само спира предложенията за замяна занапред
    void confirm({
      intent: "danger",
      icon: <DeleteOutlined />,
      title: t("inventoryPage.ichDeleteTitle", { name: ruleName }),
      explanation: t("inventoryPage.ichDeleteText"),
      preview: { caption: t("inventoryPage.ichDeleteCaption"), items: memberItems },
      note: { kind: "final", text: t("inventoryPage.ichDeleteFinal") },
      confirmLabel: t("inventoryPage.ichDeleteConfirm"),
      action: () =>
        new Promise<void>((resolve) => {
          deleteMutation.mutate(editedGroup.id, {
            onSuccess: () => {
              dirtyRef.current = false;
              message.success(t("inventoryPage.deleted"));
              resolve();
              navigate(LIST_PATH);
            },
            onError: (error: unknown) => {
              message.error(errorDetailOf(error) || t("inventoryPage.errDelete"));
              resolve();
            },
          });
        }),
    });
  };

  // ─── Клавиатура (общата на работните листове) ────────────────────────────
  // Писане където и да е извън поле влиза ДИРЕКТНО в търсачката, Esc
  // „отникъде" е стъпка назад към списъка (с пазача), Ctrl/Cmd+Enter записва,
  // ↓ „отникъде" — първото количество. Решетка няма; Esc в поле си остава на
  // полето. При отворен „Целият списък" (antd модал) клавишите са негови.
  useWorksheetKeyboard({
    isDesktop,
    searchInputRef,
    searchTextRef: searchRef,
    setSearch: updateSearch,
    focusSearch,
    focusFirst: () => {
      const first = draft.members[0];
      const target = first ? document.getElementById(amountInputId(first.key)) : searchInputRef.current;
      target?.focus();
      return !!target;
    },
    onPrimary: submit,
    onLeave: leaveToList,
    escFromFieldToSearch: false,
  });

  const title =
    mode === "edit"
      ? t("inventoryPage.editInterchangeTitle")
      : t("inventoryPage.newInterchangeTitle");
  const loading = mode === "edit" && !editedGroup && groupsLoading;

  return (
    <InventoryTheme controlHeight={40}>
      <div className="flex h-full flex-col overflow-hidden bg-canvas text-ink">
        <PageHeader
          back={{ label: t("inventoryPage.interchange"), onClick: leaveToList }}
          title={title}
          subtitle={t("inventoryPage.interchangeMembersCount", { count: draft.members.length })}
        />

        <div
          data-ich-scroll
          className={`min-h-0 flex-1 overflow-y-auto ${isNarrow ? "px-3 py-3.5" : "px-6 py-5"}`}
        >
          {loading ? (
            <div className="flex justify-center py-16">
              <Spin size="large" />
            </div>
          ) : (
            <div
              className={`mx-auto w-full max-w-[1240px] ${
                isDesktop
                  ? "grid grid-cols-[minmax(0,1fr)_minmax(300px,360px)] items-start gap-5"
                  : "flex flex-col gap-3.5"
              }`}
            >
              <InterchangeRuleChain
                members={draft.members}
                categoryOf={(productId) => productsById.get(productId)?.category ?? null}
                priorityMode={draft.priorityMode}
                sortIndexes={draft.sortIndexes}
                onPriorityModeChange={draft.setPriorityMode}
                onAmountChange={draft.setAmount}
                onMove={draft.moveMember}
                onRemove={draft.removeMember}
                availableProducts={availableProducts}
                search={search}
                onSearchChange={updateSearch}
                attachInputRef={(node) => {
                  searchInputRef.current = node;
                }}
                focusSearch={focusSearch}
                onAdd={(product) => addProducts([product])}
                onOpenCatalog={() => setCatalogOpen(true)}
              />

              <aside className={`flex flex-col gap-3.5 ${isDesktop ? "sticky top-0" : ""}`}>
                <InterchangeRulePreview
                  members={draft.members}
                  sortIndexes={draft.sortIndexes}
                  decimalsOf={decimalsOf}
                  issues={issues}
                />
                <section className="flex flex-col gap-2 rounded-xl border border-shade-2 bg-panel px-4 py-3.5">
                  <label
                    htmlFor="ich-hint"
                    className="text-[15px] font-semibold leading-[1.2] text-ink"
                  >
                    {t("inventoryPage.interchangeHintCol")}
                  </label>
                  <Input.TextArea
                    id="ich-hint"
                    value={draft.hint}
                    onChange={(event) => draft.setHint(event.target.value)}
                    placeholder={t("inventoryPage.interchangeHintPh")}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    // 16px на телефон — иначе iOS зуумва при фокус
                    className={`!rounded-[9px] ${isNarrow ? "!text-base" : "!text-[13.5px]"}`}
                  />
                </section>
              </aside>
            </div>
          )}
        </div>

        {/* Долна лента: първата пречка пред записа вляво, действията вдясно */}
        <footer
          className={`flex shrink-0 flex-wrap items-center gap-3 border-t border-shade-2 bg-panel ${
            isNarrow
              ? "px-3.5 py-3 pb-[calc(12px+env(safe-area-inset-bottom,0px)*0.5)]"
              : "px-6 py-3.5"
          }`}
        >
          {issues[0] && (
            <span className="text-[12.5px] font-semibold leading-[normal] text-brand-deep-ink">
              {issues[0]}
            </span>
          )}
          <div className="flex-1" />
          {editedGroup && (
            <button type="button" onClick={requestDelete} className={DANGER_TEXT_BUTTON_CLASS}>
              {t("inventoryPage.docDelete")}
            </button>
          )}
          <button type="button" onClick={leaveToList} className={`${SECONDARY_BUTTON_CLASS} !py-[11px]`}>
            {t("inventoryPage.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className={`${PRIMARY_BUTTON_CLASS} !px-[22px] !py-3 !text-sm ${draft.payload ? "" : "opacity-55"} ${
              isNarrow ? "flex-[1_1_100%]" : ""
            }`}
          >
            {t("inventoryPage.interchangeSubmit")}
            {!isNarrow && <KbdChip>Ctrl+Enter</KbdChip>}
          </button>
        </footer>

        <CatalogPickerModal
          open={catalogOpen}
          hint={t("inventoryPage.interchangeCatalogHint")}
          items={catalogItems}
          onClose={() => setCatalogOpen(false)}
          onAdd={addFromCatalog}
        />
      </div>
    </InventoryTheme>
  );
};

export default InterchangeableRulePage;
